/**
 * AI 成效頁的「失敗分類」——唯一一份，畫面與測試共用。
 *
 * 為什麼要另開一支（而不是在 fetch 裡直接 `throw new Error(字串)`）：
 * 以前所有非 2xx 都往上丟同一種 `Error`，畫面上就只剩一種錯誤框；使用者看到「需要權限」
 * 會以為是自己帳號壞了，於是不去重試，也不會回報。分類之後每種失敗都有對應的下一步：
 *
 *   unauthorized  401／403／沒帶 Admin Token → 要 X-Admin-Token，不是重新登入
 *   invalid       400／422            → 「查詢條件有誤」（換範圍，重試沒用）
 *   offline       5xx／斷網／timeout  → 「讀取失敗，稍後再試」＋重試
 *   error         其他（格式不對、後端設定缺）→ 「讀取失敗，回應無法顯示」＋重試
 *
 * 硬規則：**只有 401／403 可以說「權限」**。5xx、斷網、後端沒設 ADMIN_TOKEN 都是伺服器／
 * 網路的事，說成權限問題就是叫人去重新登入一個沒壞的帳號。
 */

export type AiObsFailureKind = 'unauthorized' | 'invalid' | 'offline' | 'error';

export interface AiObsFailure {
  kind: AiObsFailureKind;
  /** 給人看的句子（可以帶後端原文，但不要只有 `502 upstream_timeout`）。 */
  message: string;
  /** HTTP 狀態（斷網／前端自丟的檢查沒有）。 */
  status: number | null;
}

/** 帶著分類往上丟的錯誤；畫面用 `kind` 決定要顯示哪一種狀態。 */
export class AiObsError extends Error implements AiObsFailure {
  readonly kind: AiObsFailureKind;
  readonly status: number | null;

  constructor(failure: AiObsFailure) {
    super(failure.message);
    this.name = 'AiObsError';
    this.kind = failure.kind;
    this.status = failure.status;
  }
}

export function isAiObsError(error: unknown): error is AiObsError {
  return error instanceof AiObsError;
}

/** 斷網／連不上的字樣（fetch 自己丟的 TypeError 訊息各瀏覽器不同）。 */
const NETWORK_PATTERN = /連不到|連不上|無法連線|Failed to fetch|NetworkError|network\s*error|ERR_NETWORK|ERR_CONNECTION|ETIMEDOUT|ECONNREFUSED|ECONNRESET|ENOTFOUND|timeout|timed?\s*out/i;

/** 只有真的 401／403／未授權才配得上「權限」這兩個字。 */
const AUTH_PATTERN = /\b40[13]\b|unauthenticated|unauthorized|forbidden|登入已失效|沒有管理權限/i;

function detailOf(status: number, body: unknown, statusText: string): string {
  const raw = (body as { error?: unknown } | undefined)?.error;
  if (typeof raw === 'string' && raw.trim() !== '') return raw.trim();
  const text = statusText.trim();
  return text === '' ? String(status) : `${status} ${text}`;
}

function codeOf(body: unknown): string | null {
  const code = (body as { code?: unknown } | undefined)?.code;
  return typeof code === 'string' ? code : null;
}

/**
 * 非 2xx 的回應 → 分類好的失敗。
 * 順序：先看 401／403（權限），再看 400／422（條件），再看 5xx（後端／暫時），最後才是其他。
 */
export function failureFromResponse(status: number, body: unknown, statusText = ''): AiObsFailure {
  const detail = detailOf(status, body, statusText);
  const code = codeOf(body);

  if (status === 401 || status === 403) {
    return {
      kind: 'unauthorized',
      status,
      message: `後端拒絕 X-Admin-Token（${detail}）。登入角色 engineer 或 admin 補不上這把鑰匙，請在系統頁重貼 Admin Token。`,
    };
  }
  if (status === 400 || status === 422) {
    return {
      kind: 'invalid',
      status,
      message: `查詢條件後端不接受（${detail}）：換一個時間範圍再試。`,
    };
  }
  if (status === 503 && code === 'metrics_db_unset') {
    return {
      kind: 'error',
      status,
      message: '後端沒有接觀測庫（METRICS_DB），這一頁暫時沒有資料來源；不是你的權限問題。',
    };
  }
  if (status === 503 && code === 'admin_token_unset') {
    return {
      kind: 'error',
      status,
      message: '後端沒設定 ADMIN_TOKEN（伺服器設定），admin 端點目前是關閉的；不是你的權限問題。',
    };
  }
  if (status >= 500) {
    return {
      kind: 'offline',
      status,
      message: `後端暫時出錯（${detail}），資料沒有載入成功；等一下再試。`,
    };
  }
  return {
    kind: 'error',
    status,
    message: `讀取 AI 成效失敗（${detail}）。`,
  };
}

/** fetch 直接 reject（斷網、DNS、CORS、Worker 掛掉）→ offline。 */
export function failureFromNetwork(error: unknown): AiObsFailure {
  const raw = error instanceof Error ? error.message : String(error);
  return {
    kind: 'offline',
    status: null,
    message: `連不到後端（GET /api/admin/ai/observability）：${raw}`,
  };
}

/** 200 但內容不像端點的回應 → 一般錯誤（重試有用，換範圍沒用）。 */
export function failureFromPayload(missing: string): AiObsFailure {
  return {
    kind: 'error',
    status: null,
    message: `後端回應格式不正確（缺少 ${missing}），無法顯示；這不是「沒有資料」。`,
  };
}

/**
 * 任何丟出來的東西 → 分類。畫面只需要這一支；`AiObsError` 已經分好就直接用。
 * 沒有狀態碼時（字串錯誤）仍然靠字樣分得出 401／403 與斷網，不會全部退回「一般錯誤」。
 */
export function failureOf(error: unknown): AiObsFailure {
  if (isAiObsError(error)) return { kind: error.kind, status: error.status, message: error.message };
  const status = (error as { status?: unknown } | null)?.status;
  if (typeof status === 'number') return failureFromResponse(status, undefined);
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  if (AUTH_PATTERN.test(message)) return { kind: 'unauthorized', status: null, message };
  if (NETWORK_PATTERN.test(message)) return { kind: 'offline', status: null, message };
  return { kind: 'error', status: null, message: message === '' ? '讀取 AI 成效失敗。' : message };
}
